import { formatCurrencyInput } from '@/lib/utils/formatters';
import { useState, useEffect, useMemo } from 'react';
import type {
  AdjustmentType,
  PaymentAdjustmentModalProps,
} from '../types/modals';
import { CHARGEBACK_CONFIRM_MESSAGE } from '../constants';
import { getPaymentConfig } from '../services/applicationPaymentService';
import {
  buildRefundConfirmationMessage,
  buildExternalRefundConfirmationMessage,
  buildSubmitPayload,
  computeDefaultAdjustmentAmount,
  computeIsContractPayment,
  computePaymentCalculation,
  computeStartBalance,
  fetchPaymentLogCardNumber,
  formatCaseDate,
  formatDefaultAdjustmentAmount,
  formatPaymentChargebackAmount,
  getApplicantStatus,
  getApplicantUser,
  isContractAdjustmentAmountEditable,
  toNumber,
  validateAdjustmentBeforeConfirm,
} from '../components/modals/paymentAdjustmentModalLogic';

export const PAYMENT_ADJUSTMENT_MODAL_TID = 'application-modal-payment-adjustment';

export function usePaymentAdjustmentModal({
  onSubmit,
  onGenerateChargebackPacket,
  payment,
  application,
  autoSelectedData,
  requireNote = false,
}: Omit<PaymentAdjustmentModalProps, 'isOpen' | 'onClose'>) {
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>(null);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [applySurcharge, setApplySurcharge] = useState(false);
  const [adjustmentSubtype, setAdjustmentSubtype] = useState('');
  const [chargeRefundFee, setChargeRefundFee] = useState(false);
  const [refundProcessingFee, setRefundProcessingFee] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [isExternalRefund, setIsExternalRefund] = useState(false);
  const [externalPaymentSource, setExternalPaymentSource] = useState('');
  const [externalCheckNumber, setExternalCheckNumber] = useState('');
  const [chargebackStatus, setChargebackStatus] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [txnNumber, setTxnNumber] = useState('');
  const [caseDate, setCaseDate] = useState('');
  const [chargebackNotificationDate, setChargebackNotificationDate] =
    useState('');
  const [responseDeadline, setResponseDeadline] = useState('');
  const [chargebackAmount, setChargebackAmount] = useState('');
  const [balanceAdjustmentStatus, setBalanceAdjustmentStatus] = useState('');
  const [balanceAdjustmentAction, setBalanceAdjustmentAction] = useState('');
  const [adjustmentLeaseStatus, setAdjustmentLeaseStatus] = useState('');
  const [isCovid, setIsCovid] = useState(false);
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [isAdjustmentEditMode, setIsAdjustmentEditMode] = useState(false);
  const [amountTouched, setAmountTouched] = useState(false);
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [amountError, setAmountError] = useState('');
  const [showApplicationFeeRefundConfirm, setShowApplicationFeeRefundConfirm] =
    useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentLogCardNumber, setPaymentLogCardNumber] = useState('');
  const [paymentConfig, setPaymentConfig] = useState<Record<
    string,
    unknown
  > | null>(null);

  const paymentType = String(payment?.payment_type ?? payment?.type ?? '');
  const isContractPayment = computeIsContractPayment(paymentType);

  useEffect(() => {
    setTxnNumber(String(payment?.transaction_id ?? payment?.auth_code ?? ''));
    setCaseDate(formatCaseDate(payment?.created_at) ?? '');
    setChargebackAmount(formatPaymentChargebackAmount(payment));
    setExternalPaymentSource('');
  }, [payment]);

  useEffect(() => {
    let cancelled = false;
    setPaymentLogCardNumber('');

    async function loadPaymentLogCardNumber() {
      try {
        const cardNumber = await fetchPaymentLogCardNumber(payment);
        if (!cancelled) setPaymentLogCardNumber(cardNumber);
      } catch {
        // Confirm dialog still works without the card line.
      }
    }

    void loadPaymentLogCardNumber();
    return () => {
      cancelled = true;
    };
  }, [payment]);

  useEffect(() => {
    let cancelled = false;

    async function loadPaymentConfig() {
      try {
        const config = await getPaymentConfig();
        if (!cancelled) setPaymentConfig(config);
      } catch {
        if (!cancelled) setPaymentConfig(null);
      }
    }

    void loadPaymentConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  const startBalance = useMemo(
    () => computeStartBalance(payment, application, isContractPayment),
    [application, isContractPayment, payment],
  );

  const accessFee = toNumber(
    isContractPayment
      ? application?.access_fee
      : application?.application_fee || 35,
  );
  const balanceDue = toNumber(application?.balance_due);
  const paymentLogSurchargeFee = toNumber(
    payment?.payment_log_surcharge_fee ?? payment?.surcharge_fee,
  );
  const applicantStatus = getApplicantStatus(payment, application);
  const shouldShowApplicationFeeRefundConfirm =
    !isContractPayment &&
    adjustmentType === 'refund' &&
    adjustmentReason === 'APPLICATION_DENIED';
  const applicationFeeRefundStatusMessage = ['APPROVED', 'PENDING'].includes(
    applicantStatus.toUpperCase(),
  )
    ? 'This will change the Status back to Incomplete if you continue. '
    : '';
  const applicantUser = getApplicantUser(payment, application);
  const isRefundAdjustment =
    adjustmentType === 'refund' ||
    adjustmentType === 'refund_partial' ||
    adjustmentType === 'refund_full';
  const isExternalRefundAdjustment = isRefundAdjustment && isExternalRefund;
  const isChargebackAdjustment = adjustmentType === 'chargeback';
  const shouldUseRefundConfirmation = isRefundAdjustment && !isChargebackAdjustment;
  const shouldUseChargebackConfirmation = isChargebackAdjustment;

  const defaultAdjustmentAmount = useMemo(
    () =>
      computeDefaultAdjustmentAmount({
        adjustmentType,
        isContractPayment,
        payment,
        accessFee,
        startBalance,
        adjustmentReason,
        balanceAdjustmentAction,
        balanceDue,
        chargeRefundFee,
        refundProcessingFee,
        paymentConfig,
      }),
    [
      accessFee,
      adjustmentReason,
      adjustmentType,
      balanceAdjustmentAction,
      balanceDue,
      chargeRefundFee,
      isContractPayment,
      payment,
      paymentConfig,
      refundProcessingFee,
      startBalance,
    ],
  );

  useEffect(() => {
    setAdjustmentAmount(
      formatDefaultAdjustmentAmount(adjustmentType, defaultAdjustmentAmount),
    );
    setIsAdjustmentEditMode(false);
    setAmountTouched(false);
    setAmountError('');
  }, [adjustmentType, defaultAdjustmentAmount]);

  const isAdjustmentAmountEditable = isContractAdjustmentAmountEditable(
    adjustmentType,
    balanceAdjustmentAction,
  );

  const calculation = useMemo(
    () =>
      computePaymentCalculation({
        adjustmentType,
        adjustmentAmount,
        defaultAdjustmentAmount,
        startBalance,
        balanceDue,
        balanceAdjustmentAction,
        chargeRefundFee,
        refundProcessingFee,
        paymentSource: String(payment?.payment_source ?? ''),
        paymentConfig,
        paymentLogSurchargeFee,
      }),
    [
      adjustmentAmount,
      balanceAdjustmentAction,
      adjustmentType,
      balanceDue,
      chargeRefundFee,
      defaultAdjustmentAmount,
      payment?.payment_source,
      paymentConfig,
      paymentLogSurchargeFee,
      refundProcessingFee,
      startBalance,
    ],
  );

  const refundConfirmationMessage = buildRefundConfirmationMessage({
    payment,
    applicantUser,
    paymentType,
    adjustmentType,
    calculation,
    paymentLogCardNumber,
    application,
    chargeRefundFee,
    paymentConfig,
  });

  const externalRefundConfirmationMessage =
    buildExternalRefundConfirmationMessage(adjustmentSubtype);

  const submitConfirmMessage = isChargebackAdjustment
    ? CHARGEBACK_CONFIRM_MESSAGE
    : isExternalRefundAdjustment
      ? externalRefundConfirmationMessage
      : isRefundAdjustment && !isExternalRefund
        ? refundConfirmationMessage
        : 'Do you want to Process Payment Adjustment?';

  useEffect(() => {
    if (autoSelectedData?.adjusttype === 'increase') {
      setAdjustmentType('invoice');
      setAdjustmentReason('RENT_INCREASED');
    } else if (autoSelectedData?.adjusttype === 'decrease') {
      setAdjustmentType(isContractPayment ? 'refund_partial' : 'refund');
      setAdjustmentReason('RENT_DECREASED');
    } else if (autoSelectedData?.adjusttype === 'chgback_contract') {
      setAdjustmentType('chargeback');
      if (autoSelectedData.adjustsubtype) {
        setAdjustmentReason(autoSelectedData.adjustsubtype);
      }
    } else if (paymentType.includes('DEFAULT')) {
      // Angular: DEFAULT / DEFAULT_INVOICE payment logs open as a Default Invoice.
      setAdjustmentType('default_invoice');
      setAdjustmentReason('INVOICE_PREV_DEFAULT');
    }
  }, [autoSelectedData, isContractPayment, paymentType]);

  const handleToggle = (type: AdjustmentType) => {
    setAdjustmentType(type);
    setAdjustmentReason(type === 'default_invoice' ? 'INVOICE_PREV_DEFAULT' : '');
    setApplySurcharge(false);
    setAdjustmentSubtype('');
    setChargeRefundFee(false);
    setRefundProcessingFee(false);
    setRefundReason('');
    setIsExternalRefund(false);
    setExternalPaymentSource('');
    setExternalCheckNumber('');
    setChargebackStatus('');
    setCaseNumber('');
    setTxnNumber(String(payment?.transaction_id ?? payment?.auth_code ?? ''));
    setCaseDate(formatCaseDate(payment?.created_at) ?? '');
    setChargebackNotificationDate('');
    setResponseDeadline('');
    setChargebackAmount(formatPaymentChargebackAmount(payment));
    setBalanceAdjustmentStatus('');
    setBalanceAdjustmentAction('');
    setAdjustmentLeaseStatus('');
  };

  const runValidation = (includeNote: boolean) => {
    if (isAdjustmentEditMode && !note.trim()) {
      setError('Mandatory if Manual Adjustment is made');
      return false;
    }
    const result = validateAdjustmentBeforeConfirm({
      requireNote,
      note,
      adjustmentType,
      adjustmentAmount,
      chargebackAmount,
      includeNote,
    });
    if (!result.valid) {
      if (result.noteError) setError(result.noteError);
      if (result.amountError) setAmountError(result.amountError);
      return false;
    }
    setAmountError('');
    return true;
  };

  const handleInitialSubmit = () => {
    setError('');
    setAmountTouched(Boolean(adjustmentType));
    if (!runValidation(true)) return;
    if (shouldShowApplicationFeeRefundConfirm) {
      setShowApplicationFeeRefundConfirm(true);
      return;
    }
    setShowConfirm(true);
  };

  const handleApplicationFeeRefundContinue = () => {
    setShowApplicationFeeRefundConfirm(false);
    if (!runValidation(true)) return;
    setShowConfirm(true);
  };

  const handleFinalSubmit = async () => {
    const payload = buildSubmitPayload({
      adjustmentType,
      paymentType,
      isContractPayment,
      adjustmentReason,
      autoSelectedData,
      adjustmentSubtype,
      adjustmentAmount,
      calculation,
      chargebackAmount,
      payment,
      refundReason,
      note,
      isCovid,
      applySurcharge,
      chargeRefundFee,
      refundProcessingFee,
      isExternalRefund,
      externalPaymentSource,
      externalCheckNumber,
      chargebackStatus,
      caseNumber,
      txnNumber,
      caseDate,
      chargebackNotificationDate,
      responseDeadline,
      balanceAdjustmentStatus,
      balanceAdjustmentAction,
      adjustmentLeaseStatus,
    });

    setSubmitting(true);
    setShowConfirm(false);
    try {
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNoteChange = (value: string) => {
    setNote(value);
    if (error) setError('');
  };

  const handleAdjustmentAmountChange = (value: string) => {
    setAdjustmentAmount(formatCurrencyInput(value));
    if (amountError) setAmountError('');
  };

  const handleAdjustmentAmountBlur = () => {
    setAmountTouched(true);
    setAdjustmentAmount((value) => formatCurrencyInput(value));
  };

  const handleChargebackAmountChange = (value: string) => {
    setChargebackAmount(formatCurrencyInput(value));
  };

  const handleChargebackAmountBlur = () => {
    setChargebackAmount((value) => formatCurrencyInput(value, true));
  };

  const resolveApplicantId = () =>
    String(
      payment?.fk_applicant_id ??
        application?.applicant_id ??
        application?.user_data?.applicant_id ??
        '',
    );

  const handleGenerateChargebackPacket = async () => {
    const paymentLogId = payment?.payment_log_id;
    const applicantId = resolveApplicantId();
    if (!paymentLogId || !applicantId || !onGenerateChargebackPacket) return;

    setSubmitting(true);
    setShowConfirm(false);
    try {
      await onGenerateChargebackPacket({
        applicantId,
        paymentLogId,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const typeSectionProps = {
    tid: PAYMENT_ADJUSTMENT_MODAL_TID,
    isContractPayment,
    isDefaultPayment: paymentType.includes('DEFAULT'),
    applicantStatus,
    adjustmentType,
    onToggle: handleToggle,
    applySurcharge,
    onApplySurchargeChange: setApplySurcharge,
    adjustmentReason,
    onAdjustmentReasonChange: setAdjustmentReason,
    adjustmentSubtype,
    onAdjustmentSubtypeChange: setAdjustmentSubtype,
    chargeRefundFee,
    onChargeRefundFeeChange: setChargeRefundFee,
    refundProcessingFee,
    onRefundProcessingFeeChange: setRefundProcessingFee,
    refundReason,
    onRefundReasonChange: setRefundReason,
    isExternalRefund,
    onIsExternalRefundChange: setIsExternalRefund,
    externalPaymentSource,
    onExternalPaymentSourceChange: setExternalPaymentSource,
    externalCheckNumber,
    onExternalCheckNumberChange: setExternalCheckNumber,
    chargebackStatus,
    onChargebackStatusChange: setChargebackStatus,
    caseNumber,
    onCaseNumberChange: setCaseNumber,
    txnNumber,
    onTxnNumberChange: setTxnNumber,
    caseDate,
    onCaseDateChange: setCaseDate,
    chargebackNotificationDate,
    onChargebackNotificationDateChange: setChargebackNotificationDate,
    responseDeadline,
    onResponseDeadlineChange: setResponseDeadline,
    chargebackAmount,
    onChargebackAmountChange: handleChargebackAmountChange,
    onChargebackAmountBlur: handleChargebackAmountBlur,
    chargebackType: String(payment?.chargeback_type ?? ''),
    balanceAdjustmentStatus,
    onBalanceAdjustmentStatusChange: setBalanceAdjustmentStatus,
    balanceAdjustmentAction,
    onBalanceAdjustmentActionChange: setBalanceAdjustmentAction,
    adjustmentLeaseStatus,
    onAdjustmentLeaseStatusChange: setAdjustmentLeaseStatus,
    isCovid,
    onIsCovidChange: setIsCovid,
  };

  return {
    tid: PAYMENT_ADJUSTMENT_MODAL_TID,
    adjustmentType,
    submitting,
    handleInitialSubmit,
    typeSectionProps,
    calculatorSectionProps: {
      tid: PAYMENT_ADJUSTMENT_MODAL_TID,
      isContractPayment,
      isDefaultPayment: paymentType.includes('DEFAULT'),
      accessFee,
      calculation,
      adjustmentAmount,
      onAdjustmentAmountChange: handleAdjustmentAmountChange,
      onAdjustmentAmountBlur: handleAdjustmentAmountBlur,
      amountTouched,
      amountError,
      isAdjustmentAmountEditable,
      isAdjustmentEditMode,
      onEnterAdjustmentEditMode: () => setIsAdjustmentEditMode(true),
      showRefundFeeRows: adjustmentType === 'refund_full',
    },
    noteSectionProps: {
      tid: PAYMENT_ADJUSTMENT_MODAL_TID,
      note,
      onNoteChange: handleNoteChange,
      requireNote: isAdjustmentEditMode,
      error,
    },
    confirmDialogsProps: {
      tid: PAYMENT_ADJUSTMENT_MODAL_TID,
      showApplicationFeeRefundConfirm,
      applicationFeeRefundStatusMessage,
      onApplicationFeeRefundContinue: handleApplicationFeeRefundContinue,
      onApplicationFeeRefundDismiss: () =>
        setShowApplicationFeeRefundConfirm(false),
      showConfirm,
      shouldUseRefundConfirmation,
      shouldUseChargebackConfirmation,
      submitConfirmMessage,
      onFinalSubmit: handleFinalSubmit,
      onGenerateChargebackPacket: handleGenerateChargebackPacket,
      onConfirmDismiss: () => setShowConfirm(false),
    },
  };
}
